import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

import { InvoiceFileViewModel } from '@fora-monorepo/employer-my-pages-common/types';
import { trackException, trackTrace } from '@fora-monorepo/webapp-analytics';

import { envConfig } from '../../config';
import {
  mockBase64encodedInvoiceFilePDF,
  mockInvoiceDetailedCostsAsCSV,
  mockInvoiceEventsList,
  mockInvoiceList,
} from './mocks';
import { GetInvoiceDetailsPayload, GetInvoiceFilePayload } from './types';
import { createThunkError } from '../utils';
import { GetInvoiceEventsPayloadAction } from './invoiceSlice';

export const getInvoices = createAsyncThunk('getInvoices', async (_, thunkAPI) => {
  // export const getInvoices = createAsyncThunk('getInvoices', async (data: GetInvoicePayload, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockInvoiceList.long;
  }

  // TODO 105918 startDate and enDate will not be implemented yet
  // const { startDate, endDate } = data;

  return (
    axios
      .get(`${envConfig.bffBaseUri}/invoices`)
      // .get(`${envConfig.bffBaseUri}/invoices?startDate=${startDate}&endDate=${endDate}`)
      .then(res => {
        if (res.status === 200) {
          const invoices = res.data.invoices;
          return invoices;
        }
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError('Could not get invoices', err));
      })
  );
});

export const getInvoiceEvents = createAsyncThunk(
  'getInvoiceEvents',
  async (data: GetInvoiceDetailsPayload, thunkAPI) => {
    const { invoiceNumber } = data;
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return { invoiceNumber, eventList: mockInvoiceEventsList };
    }

    return axios
      .get(`${envConfig.bffBaseUri}/invoice/events/${invoiceNumber}`)
      .then(res => {
        if (res.status === 200) {
          return { invoiceNumber, eventList: res.data } as GetInvoiceEventsPayloadAction;
        }
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError('Could not get invoice events', err));
      });
  }
);

export const getBgNumber = createAsyncThunk('getBgNumber', async (_, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return '1111-2222';
  }

  return axios
    .get(`${envConfig.bffBaseUri}/invoice/fora-bankgiro`)
    .then(res => {
      if (res.status === 200) {
        return res.data as string;
      }
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not get bankgiro number', err));
    });
});

export const getInvoiceCsv = createAsyncThunk('getInvoiceCsv', async (data: GetInvoiceFilePayload, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockInvoiceDetailedCostsAsCSV;
  }

  const { invoiceNumber } = data;

  return axios
    .get(`${envConfig.bffBaseUri}/invoice/download/detailed-costs/${invoiceNumber}`)
    .then(res => {
      if (res.status === 200) {
        return res.data as InvoiceFileViewModel;
      } else if (res.status === 204) {
        trackTrace(`csv for invoice nr: ${data.invoiceNumber} returned 204 no content`, SeverityLevel.Warning);
        return null;
      }
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(
        createThunkError(`Could not get detailed costs as CSV for invoiceNumber: ${data.invoiceNumber}`, err)
      );
    });
});

export const getInvoiceFile = createAsyncThunk('getInvoiceFile', async (data: GetInvoiceFilePayload, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    await new Promise(resolve => setTimeout(resolve, 500));
    const data: InvoiceFileViewModel = { base64EncodedFile: mockBase64encodedInvoiceFilePDF };
    return data;
  }

  const { invoiceNumber, ocrNumber } = data;

  return axios
    .get(`${envConfig.bffBaseUri}/invoice/download/${ocrNumber ?? invoiceNumber}`)
    .then(res => {
      if (res.status === 200) {
        return res.data as InvoiceFileViewModel;
      } else if (res.status === 204) {
        trackTrace(`pdf for invoice nr: ${data.invoiceNumber} returned 204 no content`, SeverityLevel.Warning);
        return null;
      }
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(
        createThunkError(`Could not get invoice as PDF for invoiceNumber: ${invoiceNumber}`, err)
      );
    });
});
